/**
 * WhyPanel (spec §5) — evidence on demand.
 *
 * Selecting a dimension row opens this: the rationale VERBATIM (never
 * truncated on desktop; on phone it expands inline via the same panel),
 * the confidence, the recalculation date, and "based on N signals" which
 * expands to the raw signal list (source type, reliability weighting,
 * observation date) straight off the calculation row's input_formula.
 *
 * One interaction from any number — the chart row is a button, this panel
 * is what it opens (spec §12.4).
 */
import { areaXRayT, type AreaXRayLocale } from './copy'
import { formatPercent, type Contribution } from './areaScoring'

export interface WhySelection {
  contribution: Contribution
  /** Raw input_formula.components entries, when the engine recorded them. */
  signals: Array<{ source_type?: string; reliability?: number; observed_at?: string }>
  calculatedAt: string | null
}

interface WhyPanelProps {
  selection: WhySelection | null
  locale: AreaXRayLocale
}

function formatDate(iso: string | null | undefined, locale: AreaXRayLocale): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-LB-u-nu-latn' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

export function WhyPanel({ selection, locale }: WhyPanelProps) {
  if (!selection) {
    return (
      <section data-testid="why-panel" aria-label={areaXRayT('whyEyebrow', locale)}>
        <p style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-muted)' }}>
          {areaXRayT('noDimensionSelected', locale)}
        </p>
      </section>
    )
  }

  const { contribution, signals, calculatedAt } = selection
  const recalculated = formatDate(calculatedAt, locale)
  const signalDates = signals
    .map((signal) => formatDate(signal.observed_at, locale))
    .filter((value): value is string => Boolean(value))

  return (
    <section data-testid="why-panel" aria-label={areaXRayT('whyEyebrow', locale)}>
      <p style={{ margin: 0, fontFamily: 'var(--lc-font-ui)', fontWeight: 700, color: 'var(--lc-text-heading)' }}>
        {areaXRayT('whyEyebrow', locale)} — {contribution.label}
      </p>

      {/* Rationale verbatim — the engine's own words, not a summary of them. */}
      <p data-testid="why-rationale" style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-primary)' }}>
        {contribution.rationale ?? '—'}
      </p>

      <p style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-muted)' }}>
        {areaXRayT('confidenceLabel', locale)}: {formatPercent(contribution.confidence)}
        {recalculated ? ` · ${areaXRayT('recalculated', locale, { date: recalculated })}` : ''}
      </p>

      <details data-testid="why-signals">
        <summary style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-action-primary)', cursor: 'pointer' }}>
          {areaXRayT('basedOnSignals', locale, { count: signals.length })}
        </summary>
        {signals.length ? (
          <ul style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-secondary)', paddingInlineStart: 20 }}>
            {signals.map((signal, index) => (
              <li key={index} data-testid={`signal-${index}`}>
                {signal.source_type ?? '—'}
                {typeof signal.reliability === 'number'
                  ? ` · ${areaXRayT('signalReliability', locale)} ${signal.reliability.toFixed(2)}`
                  : ''}
                {signalDates[index] ? ` · ${areaXRayT('signalDate', locale)} ${signalDates[index]}` : ''}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-muted)' }}>—</p>
        )}
      </details>
    </section>
  )
}
